<template>
  <div class="table-view">
    <el-table
      ref="tableComponent"
      border
      highlight-current-row
      :height="height"
      :row-key="rowKey || undefined"
      :show-summary="isSummary"
      :cell-style="customCellStyle"
      :summary-method="getSummaries"
      :row-class-name="customRowClassName"
      :cell-class-name="customCellClassName"
      :header-cell-class-name="customHeaderClassName"
      :data="data"
      :scrollbar-always-on="true"
      :expand-row-keys="expandRowKeys?.length ? expandRowKeys : undefined"
      @cell-click="columnClick"
      @row-click="rowClick"
    >
      <slot name="custom-first-column" />
      <el-table-column
        v-for="(item, index) in tableColumns"
        :key="index"
        :prop="item.field"
        :label="item.label"
        :fixed="index < fixedColumnCounts"
        :sortable="!item.isList && !item.isDisableSort"
        :width="item.width ? item.width : null"
        :min-width="item.minWidth ? item.minWidth : null"
      >
        <template
          v-if="item.tooltip"
          #header
        >
          <template v-if="item.tooltip.showIcon === false">
            <el-tooltip
              effect="dark"
              placement="top"
              :content="item.tooltip.content"
            >
              <span class="table-header-tooltip-label">{{ item.label }}</span>
            </el-tooltip>
          </template>
          <template v-else>
            {{ item.label }}
            <el-tooltip
              effect="dark"
              placement="top"
              :content="item.tooltip.content"
            >
              <i class="fa fa-info-circle table-header-tooltip-icon" />
            </el-tooltip>
          </template>
        </template>

        <template #default="scope">
          <read-more
            v-if="item.isReadMore"
            more-str="more"
            class="read-more"
            :more-txt-class="item.moreTxtClass"
            :class="[
              item.isCenterAlign ? 'text-center' : '',
              item.isCapitalize ? 'text-capitalize' : '',
              item.customClass ? `${item.customClass}` : '',
            ]"
            :text="scope.row[item.field]"
            :max-chars="item.maxLength || 40"
            :is-plain-text="item.isPlainText"
            @expand="resizeRowHeight(scope.$index)"
          />

          <div v-else-if="item.isList">
            <div
              v-if="scope.row[item.field] && scope.row[item.field].comment"
              class="list-comment"
            >
              {{ scope.row[item.field].comment }}
            </div>
            <ol
              v-if="scope.row[item.field] && scope.row[item.field].list && scope.row[item.field].list.length > 0"
              :class="[item.isCapitalize ? 'text-capitalize' : '', item.customClass ? `${item.customClass}` : '']"
            >
              <li
                v-for="(change, ind) in scope.row[item.field].list"
                :key="ind"
              >
                <div v-dompurify-html="change ?? ''" />
              </li>
            </ol>
          </div>

          <div v-else-if="item.isDropdown">
            <dropdown-list
              v-model="scope.row[item.field]"
              :options="item.dropdownList"
              @change="chooseDropdown($event, scope.$index, item.field)"
            />
          </div>

          <div v-else-if="item.isCheckbox">
            <input
              v-model="scope.row[item.field]"
              type="checkbox"
              :class="item.customClass"
              @change="onChangeChkBox($event, scope.$index, item.field)"
            >
          </div>

          <div v-else-if="item.isTextfield">
            <textarea
              v-model="scope.row[item.field]"
              :class="item.customClass"
            />
          </div>

          <div
            v-else
            :class="[
              item.isCenterAlign ? 'text-center' : '',
              item.isCapitalize ? 'text-capitalize' : '',
              item.customClass ? `${item.customClass}` : '',
            ]"
          >
            <i
              v-if="item.field === 'icon'"
              class="info-icon"
              :class="item.icon"
            />
            <span v-if="item.isPlainText">{{ formatText(item, scope.row[item.field], scope.row) }}</span>
            <span
              v-else
              v-dompurify-html="formatText(item, scope.row[item.field], scope.row) ?? ''"
            />
          </div>
        </template>
      </el-table-column>
      <slot name="custom-columns" />
      <slot name="right-fixed-columns" />
    </el-table>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, watch, PropType } from 'vue';
import delay from 'yoctodelay';
import util from '@/Utils/util';
import { TableColumnType, HighlightTableItemType } from '@/models/table';
import { commonDateTimeFormat } from '@/constants/app';

const props = defineProps({
  data: {
    type: Array<any>,
    default: () => [],
  },

  tableColumns: {
    type: Array as PropType<TableColumnType[]>,
    default: () => [],
  },

  height: {
    type: Number,
    default: 450,
  },

  fixedColumnCounts: {
    type: Number,
    default: 0,
  },

  selectedIndex: {
    type: Number,
    default: -1,
  },

  pageId: {
    type: String,
    default: '',
  },

  isSummary: {
    type: Boolean,
    default: false,
  },

  getSummaries: {
    type: Function,
    default: () => null,
  },

  highlightList: {
    type: Array as PropType<HighlightTableItemType[]>,
    default: () => [],
  },

  rowKey: {
    type: String,
    default: '',
  },

  customCellStyleFunc: {
    type: Function,
    default: () => null,
  },

  expandRowKeys: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const emit = defineEmits(['row-click', 'column-click', 'change-column']);

const isCopy = ref(true);
const tableComponent: Ref<any> = ref(null);

const formatText = (column: TableColumnType, value: string, rowData: any) => {
  if (column.isDateField) {
    return util.formatDateTime(value, commonDateTimeFormat);
  }

  if (column.formatter) {
    return column.formatter(value, rowData);
  }

  return value;
};

const columnClick = (row: any, column: any, cell: any, event: any) => {
  emit('column-click', row, column, cell, event, isCopy.value);
  isCopy.value = true;
};

const rowClick = (row: any, column: any) => {
  emit('row-click', row, column);
};

const resizeRowHeight = async (index: any) => {
  isCopy.value = false;

  await delay(100);

  const tableContainer = document.querySelector(`.${props.pageId} .table-view .el-table`) as HTMLElement;
  if (tableContainer) {
    const contentRows = tableContainer.querySelectorAll('.el-table__body-wrapper tbody .el-table__row') as NodeListOf<HTMLElement>;
    const currentRow = contentRows.item(index);

    const fixedLeft = tableContainer.querySelector('.el-table__fixed') as HTMLElement;
    if (fixedLeft) {
      const fixedLeftRows = fixedLeft.querySelectorAll('.el-table__row') as NodeListOf<HTMLElement>;
      const fixedLeftCurrentRow = fixedLeftRows.item(index);

      if (currentRow.offsetHeight !== fixedLeftCurrentRow.offsetHeight) {
        fixedLeftCurrentRow.style.height = `${currentRow.offsetHeight}px`;
      }
    }

    const fixedRight = tableContainer.querySelector('.el-table__fixed-right') as HTMLElement;
    if (fixedRight) {
      const fixedRightRows = fixedRight.querySelectorAll('.el-table__row') as NodeListOf<HTMLElement>;
      const fixedRightCurrentRow = fixedRightRows.item(index);

      if (currentRow.offsetHeight !== fixedRightCurrentRow.offsetHeight) {
        fixedRightCurrentRow.style.height = `${currentRow.offsetHeight}px`;
      }
    }

    tableComponent.value?.doLayout();
  }
};

const customCellStyle = (param: any) => {
  if (!props.customCellStyleFunc) {
    return null;
  }

  return props.customCellStyleFunc(param);
};

const customHeaderClassName = (param: any) => {
  const { columnIndex } = param;

  return props.tableColumns[columnIndex]?.customHeaderClass || '';
};

const customRowClassName = (param: any) => {
  const { row, rowIndex } = param;
  const highlightIndex = row.orderIndex !== undefined ? row.orderIndex : rowIndex;

  const customRowClassNames = props.highlightList
    .filter((item: HighlightTableItemType) => item.index === highlightIndex && item.type === 'row')
    .map((item: HighlightTableItemType) => item.class);

  const customRowClass = customRowClassNames.join(' ') + (rowIndex === props.selectedIndex ? ' selected-row' : '');

  return customRowClass;
};

const customCellClassName = (param: any) => {
  const { column, rowIndex } = param;
  const customCellClassNames = props.highlightList
    .filter((item: HighlightTableItemType) => item.index === rowIndex && item.field === column.property && item.type === 'column')
    .map((item: HighlightTableItemType) => item.class);

  return customCellClassNames.join(' ');
};

const chooseDropdown = (value: string | number, rowIndex: number, columnField: string) => {
  emit('change-column', value, rowIndex, columnField);
};

const onChangeChkBox = (event: Event, rowIndex: number, field: string) => {
  const target = event.target as HTMLInputElement;
  const isChecked = target.checked;
  emit('change-column', isChecked, rowIndex, field);
};

const toggleRowExpansion = (row: any, expanded?: boolean) => {
  tableComponent.value?.toggleRowExpansion(row, expanded);
};

watch(
  () => props.data,
  (value: any) => {
    if (!props.pageId || props.isSummary) {
      return;
    }

    if (!value || value.length === 0) {
      return;
    }

    const tableContainer = document.querySelector(`.${props.pageId} .table-view .el-table`) as HTMLElement;
    if (tableContainer) {
      const fixedLeft = tableContainer.querySelector('.el-table__fixed') as HTMLElement;
      if (fixedLeft) {
        fixedLeft.style.height = `${tableContainer.offsetHeight - 15}px`;
      }

      const fixedRight = tableContainer.querySelector('.el-table__fixed-right') as HTMLElement;
      if (fixedRight) {
        fixedRight.style.height = `${tableContainer.offsetHeight - 15}px`;
      }
    }
  },
);

defineExpose({ toggleRowExpansion });
</script>

<style lang="scss">
.table-view {
  background: #fff;
  a {
    color: #0b40c1;
    cursor: pointer;
    &:focus {
      color: #0b40c1;
    }
    &:hover {
      color: #f00;
    }
  }
  ol {
    margin-left: 5px;
    margin-bottom: 0;
    padding-left: 20px;
    li {
      text-align: left;
      padding-left: 5px;
    }
    &.text-list {
      list-style: none;
      counter-reset: item;
      li {
        margin-top: 10px;
        counter-increment: item;
        position: relative;
        &::before {
          content: counter(item) '. ';
          color: red;
          font-weight: bold;
          position: absolute;
          left: -10px;
        }
        &:first-child {
          margin-top: 0;
        }
      }
    }
  }
  .active {
    color: #f00;
    &.arrow {
      position: relative;
      &:after {
        border-left: 8px solid #ec0f0f;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        content: '';
        display: inline-block;
        position: absolute;
        right: 5px;
        opacity: 1;
        top: 4px;
        transition: opacity 150ms ease-in;
        @media (max-width: 576px) {
          display: none;
        }
      }
    }
  }
  .info-icon {
    width: 30px;
    height: 30px;
    font-size: 22px;
    line-height: 28px;
    cursor: pointer;
    margin: 0 auto;
    text-align: center;
    padding-left: 2px;
    &.border {
      border: 2px solid #ee682d !important;
      border-radius: 50%;
    }
  }
  .read-more {
    p {
      margin-bottom: 0;
    }
    a {
      padding-left: 0;
    }
  }
  .el-table {
    z-index: 0;
  }
  .el-table__body {
    margin-bottom: 0;
    tr.selected-row {
      td,
      &:hover > td,
      &.current-row > td,
      &.hover-row > td {
        background-color: #90ee90 !important;
        color: #252422 !important;
      }
    }
    tbody {
      tr.light-pink-background {
        background-color: #ffb6c1 !important;
        td,
        &:hover > td,
        &.current-row > td,
        &.hover-row > td {
          background-color: #ffb6c1 !important;
        }
      }
      tr.orange-background {
        background-color: #faa502 !important;
        td,
        &:hover > td,
        &.current-row > td,
        &.hover-row > td {
          background-color: #faa502 !important;
        }
      }
      tr.gray-background {
        background-color: gray !important;
        color: #fff !important;
        td,
        &:hover > td,
        &.current-row > td,
        &.hover-row > td {
          background-color: gray !important;
          color: #fff !important;
        }
      }
      tr,
      tr.hover-row,
      tr.current-row {
        td.light-pink-background {
          background-color: #ffb6c1 !important;
        }
        td.orange-background {
          background-color: #faa502 !important;
        }
        td.gray-background {
          background-color: gray !important;
          color: #fff !important;
        }
      }
      tr.selected-row {
        background-color: #90ee90 !important;
        color: #252422 !important;
        td,
        &:hover > td,
        &.current-row > td,
        &.hover-row > td {
          background-color: #90ee90 !important;
          color: #252422 !important;
        }
      }
    }
  }
  .el-table thead tr th:first-child {
    text-align: center;
    padding: 0;
  }
  .el-table thead.is-group th,
  .el-table th.is-leaf {
    font-size: 12px;
    text-align: center;
    padding: 0;
    border-right: 1px solid #ebeef5;
    text-transform: capitalize;
  }
  .el-table td {
    color: #252422;
    font-size: 12px;
    padding: 0 5px;
    border-right: 1px solid #ebeef5;
    &.actions {
      .btn {
        min-width: unset;
      }
    }
  }
  .el-table--border th:first-child .cell {
    padding-left: 5px;
  }
  .el-table--border td:first-child .cell {
    padding-left: 5px;
    display: flex;
    align-items: center;
    .text-center {
      width: 100%;
    }
  }
  .el-table thead.is-group th {
    border-bottom: 1px solid #ebeef5;
  }
  .el-table .cell,
  .el-table th > .cell {
    padding: 5px;
    word-break: break-word;
  }
  .el-table th > .cell {
    padding: 9px;
    white-space: nowrap;
    word-break: keep-all;
    text-align: left;
  }
  .el-table {
    &::before,
    &::after {
      z-index: 0;
    }
    .el-table__fixed,
    .el-table__fixed-right {
      pointer-events: none;
      z-index: 0;
      @media (max-width: 992px) {
        height: 100% !important;
        .el-table__row .cell,
        .el-table__header th > .cell {
          padding: 5px 0;
        }
      }
      .el-table__fixed-body-wrapper {
        button {
          @media (max-width: 992px) {
            min-width: unset;
            padding: 7px 3px !important;
          }
        }
      }
    }
    .el-table__header-wrapper {
      .el-table-fixed-column--left,
      .el-table-fixed-column--right {
        background: #fff !important;
        .cell {
          background: #fff;
        }
      }
      pointer-events: all;
    }
    .el-table__body-wrapper {
      .el-table-fixed-column--left,
      .el-table-fixed-column--right {
        background: #fff !important;
        .cell {
          background: #fff;
        }
      }
      .el-scrollbar__bar {
        &.is-horizontal {
          height: 10px;
        }
        &.is-vertical {
          width: 10px;
        }
      }
    }
  }
  .table-header-tooltip-icon {
    margin-left: 6px;
    color: #5a4281;
    font-size: 14px;
    cursor: help;
    transition: color 0.15s ease;

    &:hover {
      color: #6d509c;
    }
  }

  .table-header-tooltip-label {
    cursor: help;
    border-bottom: 1px dotted #909399;
  }
}
</style>
